import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  Image as CitrusImage,
  JustifyContent,
  withCitrusTheme,
  Heading6,
  Body2,
} from '@gousto-internal/citrus-react'

import { getProductImage } from './BasketUpsell/image'
import type { BasketUpsellProduct, BasketUpsellProductActionSource } from './BasketUpsell/types'
import { activateOnKeyboard } from './keyboard'
import { QuantityControl } from './QuantityControl'

const ProductImage = withCitrusTheme(CitrusImage, () => ({
  objectFit: 'cover',
  objectPosition: 'center',
  borderRadius: '3px',
}))

type ProductRowProps = {
  product: BasketUpsellProduct
  onClickProduct: (product: BasketUpsellProduct) => void
  onAddProduct: (productId: string, source: BasketUpsellProductActionSource) => void
  onRemoveProduct: (productId: string, source: BasketUpsellProductActionSource) => void
}

export const ProductItem = ({
  product,
  onAddProduct,
  onClickProduct,
  onRemoveProduct,
}: ProductRowProps) => {
  const image = getProductImage(product)
  const isUnavailable = product.outOfStock || !product.isAvailable || !!product.limitReached
  const handleProductClick = () => onClickProduct(product)
  const handleProductKeyDown = (event: React.KeyboardEvent) =>
    activateOnKeyboard(event, handleProductClick)

  return (
    <Box
      key={product.id}
      display={Display.Flex}
      justifyContent={JustifyContent.FlexStart}
      alignItems={AlignItems.Center}
      gap={3}
      color={Color.ColdGrey_900}
      data-testid="product"
      style={{ cursor: 'pointer' }}
      onClick={handleProductClick}
      onKeyDown={handleProductKeyDown}
      role="button"
      tabIndex={0}
    >
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.Center}
        height="70px"
        width="88px"
      >
        {image.src ? (
          <ProductImage
            alt={image.alt}
            loading="lazy"
            sizes="88px"
            src={image.src}
            srcSet={image.srcSet}
            height="70px"
            width="88px"
          />
        ) : (
          <Box bg={Color.ColdGrey_50} height="70px" width="88px" />
        )}
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.FlexStart}
        flexGrow={1}
        height="100%"
        width="100px"
      >
        <Heading6>{product.title}</Heading6>
        <Box
          display={Display.Flex}
          gap={1}
          color={Color.ColdGrey_600}
          style={{ whiteSpace: 'nowrap' }}
        >
          <Body2 color={Color.Inherit}>
            {product.listPrice}
            {product.portion?.label && ` • ${product.portion.label}`}
          </Body2>
        </Box>
      </Box>
      <QuantityControl
        count={product.qty}
        decrementIcon={product.qty > 1 ? 'remove' : 'delete_outline'}
        decrementLabel={product.qty > 1 ? 'Remove item' : 'Delete item'}
        onRemove={() => onRemoveProduct(product.id, 'basket')}
        onAdd={() => onAddProduct(product.id, 'basket')}
        addDisabled={isUnavailable}
      />
    </Box>
  )
}
